import { apiService } from './api'
import { Permission, Role } from '../types'

export interface PermissionCheckParams {
  permission: string
  contextType?: string
  contextId?: number
}

export interface AssignRoleData {
  role_name: string
  expires_at?: string
}

export interface CreatePermissionData {
  name: string
  display_name: string
  description?: string
  resource: string
  action: string
}

export interface CreateRoleData {
  name: string
  display_name: string
  description?: string
  permission_ids?: number[]
}

export class PermissionService {
  // Permission management
  async getPermissions(): Promise<{ permissions: Permission[], grouped: Record<string, Permission[]> }> {
    return apiService.get('/permissions')
  }

  async createPermission(data: CreatePermissionData): Promise<Permission> {
    return apiService.post('/permissions', data)
  }

  async updatePermission(id: number, data: Partial<CreatePermissionData>): Promise<void> {
    return apiService.put(`/permissions/${id}`, data)
  }

  async deletePermission(id: number): Promise<void> {
    return apiService.delete(`/permissions/${id}`)
  }

  // Role management
  async getRoles(): Promise<Role[]> {
    return apiService.get('/roles')
  }

  async createRole(data: CreateRoleData): Promise<Role> {
    return apiService.post('/roles', data)
  }

  async updateRole(id: number, data: Partial<CreateRoleData>): Promise<void> {
    return apiService.put(`/roles/${id}`, data)
  }

  async deleteRole(id: number): Promise<void> {
    return apiService.delete(`/roles/${id}`)
  }

  // User permission management
  async getUserPermissions(userId: number): Promise<{ permissions: Permission[], roles: Role[] }> {
    return apiService.get(`/users/${userId}/permissions`)
  }

  async assignUserRole(userId: number, data: AssignRoleData): Promise<void> {
    return apiService.post(`/users/${userId}/roles`, data)
  }

  async removeUserRole(userId: number, roleName: string): Promise<void> {
    return apiService.delete(`/users/${userId}/roles/${roleName}`)
  }

  // Permission checking
  async checkPermission(params: PermissionCheckParams): Promise<{ has_permission: boolean }> {
    const queryParams = new URLSearchParams({
      permission: params.permission,
      ...(params.contextType && { context_type: params.contextType }),
      ...(params.contextId && { context_id: params.contextId.toString() })
    })
    
    return apiService.get(`/permissions/check?${queryParams}`)
  }

  // Client-side permission checking (requires permissions to be loaded)
  hasPermission(userPermissions: Permission[], permissionName: string): boolean {
    return userPermissions.some(p => p.name === permissionName)
  }

  hasRole(userRoles: Role[], roleName: string): boolean {
    return userRoles.some(r => r.name === roleName)
  }

  hasAnyRole(userRoles: Role[], roleNames: string[]): boolean {
    return roleNames.some(roleName => this.hasRole(userRoles, roleName))
  }

  hasResourcePermission(userPermissions: Permission[], resource: string, action: string): boolean {
    return this.hasPermission(userPermissions, `${resource}.${action}`)
  }

  // Permission helpers
  canCreate(userPermissions: Permission[], resource: string): boolean {
    return this.hasResourcePermission(userPermissions, resource, 'create')
  }

  canRead(userPermissions: Permission[], resource: string): boolean {
    return this.hasResourcePermission(userPermissions, resource, 'read')
  }

  canUpdate(userPermissions: Permission[], resource: string): boolean {
    return this.hasResourcePermission(userPermissions, resource, 'update')
  }

  canDelete(userPermissions: Permission[], resource: string): boolean {
    return this.hasResourcePermission(userPermissions, resource, 'delete')
  }

  // Admin checks
  isAdmin(userRoles: Role[]): boolean {
    return this.hasAnyRole(userRoles, ['super_admin', 'admin'])
  }

  isSuperAdmin(userRoles: Role[]): boolean {
    return this.hasRole(userRoles, 'super_admin')
  }

  // Resource-specific permission helpers
  canManageTransactions(userPermissions: Permission[]): boolean {
    return this.hasAnyPermission(userPermissions, [
      'transactions.create',
      'transactions.update',
      'transactions.delete'
    ])
  }

  canManageCategories(userPermissions: Permission[]): boolean {
    return this.hasAnyPermission(userPermissions, [
      'categories.create',
      'categories.update',
      'categories.delete'
    ])
  }

  canManageGroups(userPermissions: Permission[]): boolean {
    return this.hasAnyPermission(userPermissions, [
      'groups.create',
      'groups.update',
      'groups.delete',
      'groups.manage_members'
    ])
  }

  canManageSystem(userPermissions: Permission[]): boolean {
    return this.hasAnyPermission(userPermissions, [
      'system.manage_roles',
      'system.manage_permissions',
      'system.view_logs'
    ])
  }

  private hasAnyPermission(userPermissions: Permission[], permissionNames: string[]): boolean {
    return permissionNames.some(name => this.hasPermission(userPermissions, name))
  }

  // Group permissions by resource for UI display
  groupPermissionsByResource(permissions: Permission[]): Record<string, Permission[]> {
    return permissions.reduce((acc, permission) => {
      const resource = permission.resource
      if (!acc[resource]) {
        acc[resource] = []
      }
      acc[resource].push(permission)
      return acc
    }, {} as Record<string, Permission[]>)
  }

  // Get available actions for a resource
  getResourceActions(permissions: Permission[], resource: string): string[] {
    return permissions
      .filter(p => p.resource === resource)
      .map(p => p.action)
      .filter((action, index, arr) => arr.indexOf(action) === index) // unique
  }

  // Format permission name for display
  formatPermissionName(permission: Permission): string {
    return permission.display_name || `${permission.resource}.${permission.action}`
  }

  // Check if permission is system-level
  isSystemPermission(permission: Permission): boolean {
    return permission.resource === 'system'
  }

  // Get permission level (basic, advanced, system)
  getPermissionLevel(permission: Permission): 'basic' | 'advanced' | 'system' {
    if (permission.resource === 'system') return 'system'
    if (['create', 'read'].includes(permission.action)) return 'basic'
    return 'advanced'
  }
}

export const permissionService = new PermissionService()